<script setup lang="ts" generic="T">

type OptionType = OptionValue<T> & { icon?: { name: string; size?: number }; description?: string };

type BaseTypeSize = 'default';

const props = withDefaults(defineProps<{
    isDisabled?: boolean;
    items: OptionType[];
    modelValue: OptionType['value'];
    isLoading?: boolean;
    size?: BaseTypeSize;
    labelHeight?: number;
    needTips?: boolean;
}>(), {
    isDisabled: false,
    isLoading: false,
    size: 'default',
    labelHeight: undefined,
    needTips: true
});

defineOptions({
    inheritAttrs: false
});

const emit = defineEmits<{
    (e: 'update:modelValue', payload: OptionType['value']): void;
}>();

function handleChange(val: OptionType['value']) {
    emit('update:modelValue', val);
}

</script>

<template>
  <nav class="display-flex justify-between controls"
       v-bind="$attrs"
  >
    <Transition mode="out-in"
                name="fade">
      <div class="grow display-flex"
           v-if="isLoading">
        <LoaderSkeleton v-for="(_, index) in items"
                        :key="index"
                        :height="labelHeight || 42"
                        class="grow" />
      </div>
      <div class="grow display-flex"
           v-else>
        <label v-for="(item, index) in items"
               :title="needTips ? item.value as string : undefined"
               :key="index"
               :for="item.label"
               :style="labelHeight ? { height: `${labelHeight}px` } : undefined"
               :class="[
                 'controls-label display-flex align-items-center justify-center',
                 `async-controls-label--${size}`,
                 { 'control-active': item.value === modelValue } ]"
               :aria-disabled="isDisabled"
        >
          <input type="radio"
                 class="cursor-pointer"
                 :class="{ 'pointer-events-none': isDisabled }"
                 :aria-label="`tab ${item.label}`"
                 :disabled="isDisabled"
                 :value="item.value"
                 :checked="item.value === modelValue"
                 @change="() => handleChange(item.value)"
          >
          <div class="grow align-self-stretch"
               v-if="item.description && item.label">
            <slot name="custom"
                  :item="item"
            >      </slot>
          </div>
          <span v-else-if="item.label"
                class="compact-500-accent"
          >{{ item.label }}</span>
          <IconMain v-if="item?.icon"
                    :size="item.icon.size ?? 24"
                    :class="{ 'svg-fill-polygon': item.value === 'matic' }"
                    fill="currentColor"
                    :icon="item.icon.name"
          />
        </label>
      </div>
    </Transition>
  </nav>
</template>

<style lang="scss" scoped>

.async-controls-label {
  &--default {
    padding-top: 9px;
    padding-bottom: 9px;
  }
}

.controls {
  display: flex;
  padding: 4px;
  border: 1px solid var(--border-primary);
  border-radius: 4px;
  background: var(--bg-secondary);

  &-label {
    position: relative;
    flex-grow: 1;
    transition: background 0.3s ease;
    border-radius: 2px;
    background: var(--bg-secondary);
    user-select: none;

    input[type="radio"] {
      display: block;
      position: absolute;
      z-index: 1;
      width: 100%;
      height: 100%;
      opacity: 0;
    }

    &.control-active {
      background: var(--bg-tertary);
    }
  }
}

:deep(.svg-fill-polygon) {
  path {
    fill: var(--content-secondary);
  }
}

.fade-enter-active {
  transition: all 0.2s ease-out;
}

.fade-leave-active {
  transition: all 0.3s cubic-bezier(1, 0.8, 0.9, 1);
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

</style>
